import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import ContactForm from '../../components/ContactForm';
import { fetchLocalizedCollection } from '../../lib/cms';

export default function ContactPageEn() {
  const [searchParams] = useSearchParams();
  const presserParam = searchParams.get('presser');
  const [pageData, setPageData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('virksomhedsoplysninger', 'en').then((items) => {
      if (items && items.length) setCompanyInfo(items[0]);
    });
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'kontakt' || getSlugVal(s) === 'contact'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Contact us';
  const badge = pageData?.undertitel || 'Dialogue & Technical Advisory';
  const intro =
    pageData?.intro ||
    'Contact PMB for technical advice, spare parts inquiry, or a quotation. We specialize in presses, sheet metal processing, and centralized lubrication.';
  const seoTitle =
    pageData?.seo_titel || 'PMB Contact – Machinery, Lubrication & Technical Support';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'Contact POVL MØLLERS MASKINFABRIK A/S in Borup, Denmark. Reach our machinery, central lubrication, and service specialists by phone or email.';

  const defaultSubject = useMemo(() => {
    if (presserParam) {
      return `Inquiry regarding: ${presserParam}`;
    }
    return '';
  }, [presserParam]);

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Contact</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              Our engineers and advisors are ready to assist you. We look forward to cooperating with you!
            </p>
          </div>
        </div>
      </section>

      {/* Main Content: Info cards + Form */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Direct Info Cards */}
            <div className="lg:col-span-5 space-y-6">
              {/* Primary Contact Info Card */}
              <div className="bg-white rounded-lg border border-stone-200 p-6 sm:p-8 shadow-sm space-y-6">
                <h3 className="text-xl font-bold text-stone-900 border-b border-stone-100 pb-4">
                  Company Information
                </h3>

                <div className="space-y-5 text-sm">
                  {/* Firma */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        Company Name
                      </span>
                      <strong className="text-stone-900 text-base font-bold">
                        {companyInfo?.firmanavn || 'POVL MØLLERS MASKINFABRIK A/S'}
                      </strong>
                    </div>
                  </div>

                  {/* Telefon */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        Telephone
                      </span>
                      <a
                        href={`tel:${(companyInfo?.telefon || '57526262').replace(/\s+/g, '')}`}
                        className="text-lg font-extrabold text-[#006368] hover:text-[#004d51] transition whitespace-nowrap block"
                      >
                        {companyInfo?.telefon || '+45 57 52 62 62'}
                      </a>
                      <span className="text-xs text-stone-500">Direct phone line</span>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        E-mail
                      </span>
                      <a
                        href={`mailto:${companyInfo?.email || 'info@pmborup.dk'}`}
                        className="text-base font-bold text-stone-900 hover:text-[#006368] transition break-all block"
                      >
                        {companyInfo?.email || 'info@pmborup.dk'}
                      </a>
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        Visiting Address
                      </span>
                      <p className="text-stone-800 font-medium">
                        {companyInfo?.adresse || 'Kimmerslevvej 3'}<br />
                        {companyInfo?.postnr_by || '4140 Borup'}<br />
                        Denmark
                      </p>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-id-card" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        CVR / VAT Number
                      </span>
                      <p className="text-stone-800 font-medium">
                        DK {companyInfo?.cvr || '52043018'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Opening hours card */}
              <div className="bg-white rounded-lg border border-stone-200 p-6 shadow-sm">
                <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-clock text-[#006368]" />
                  <span>Business Hours</span>
                </h4>
                <div className="space-y-2 text-xs sm:text-sm text-stone-700">
                  <div className="flex justify-between border-b border-stone-100 pb-1.5">
                    <span>Monday – Thursday:</span>
                    <strong className="text-stone-900">07:00 – 15:30</strong>
                  </div>
                  <div className="flex justify-between border-b border-stone-100 pb-1.5">
                    <span>Friday:</span>
                    <strong className="text-stone-900">07:00 – 12:00</strong>
                  </div>
                  <div className="flex justify-between text-stone-500">
                    <span>Weekend & Holidays:</span>
                    <span>Closed</span>
                  </div>
                </div>
                <p className="text-[11px] text-stone-500 mt-4 italic">
                  * On-site emergency technical service available 24/7 by prior customer service agreement.
                </p>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm defaultSubject={defaultSubject} lang="en" />
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Map Section */}
      <section className="h-96 w-full relative bg-stone-200">
        <iframe
          title="POVL MØLLERS MASKINFABRIK A/S Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2258.972322301132!2d11.96985017730997!3d55.48057997327315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46525974cf9787c1%3A0xb695fcaab29944a9!2sKimmerslevvej%203%2C%204140%20Borup!5e0!3m2!1sen!2sdk!4v1700000000000!5m2!1sen!2sdk"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="grayscale contrast-125 filter"
        />
      </section>
    </>
  );
}
